import numeral from 'numeral';
import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import {
  SAVE_ADJUSTMENT_SUCCESS,
  SAVE_ADJUSTMENT,
  SAVE_ADJUSTMENT_FAILURE,
  REMOVE_ADJUSTMENT,
  REMOVE_ADJUSTMENT_SUCCESS,
  REMOVE_ADJUSTMENT_FAILURE,
  FETCH_ADJUSTMENT_ATTACHMENTS,
  FETCH_ADJUSTMENT_ATTACHMENTS_SUCCESS,
  FETCH_ADJUSTMENT_ATTACHMENTS_FAILURE,
  FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL,
  FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL_SUCCESS,
  FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL_FAILURE,
} from './types';
import {
  addAttachments as addAttachmentsApi,
  addAdjustment as addAdjustmentApi,
  updateAdjustment as updateAdjustmentApi,
  removeAdjustment as removeAdjustmentApi,
  fetchAdjustmentAttachments as fetchAdjustmentAttachmentsApi,
} from '../../../shared/api';
import { fetchBookTypes, fetchProvisionData, fetchAccounts, fetchingError } from '../../../shared/store/actions';
import { successNotification, errorNotification } from '../../../shared/store/notificationActions';
import getModalActions from '../../../shared/modals/getModalActions';
import { ModalNames } from '../../constants';
import agGridConfig from '../../../agGridConfig';

const {
  showAction: showAdjustmentModalForAddEdit,
  hideAction: hideAdjustmentModalForAddEdit,
} = getModalActions(ModalNames.AdjustmentAddEdit);

const {
  showAction: showDeleteModal,
  hideAction: hideDeleteModal,
} = getModalActions(ModalNames.AdjustmentDelete);

export {
  showAdjustmentModalForAddEdit,
  hideAdjustmentModalForAddEdit,
  showDeleteModal,
  hideDeleteModal,
};

const getBookType = (adjustmentTypes, adjustmentData) => {
  const chosenAdjustmentType = adjustmentTypes.find(
    adjustmentType => adjustmentType.value === adjustmentData.adjustmentType
  );
  const { bookType } = chosenAdjustmentType;
  return bookType;
};

const mapAdjustmentData = adjustmentData => ({
  ...adjustmentData,
  amount: numeral(adjustmentData.amount).value(),
});

export const saveAdjustment = ({ params, adjustmentData, openForEdit, leaveModalOpen }, callback) => async dispatch => {
  dispatch({
    type: SAVE_ADJUSTMENT,
  });
  const period = params.period;
  const companyCode = params.companyCode;
  let adjustmentId = adjustmentData.adjustmentId;
  const bookType = getBookType(
    params.adjustmentTypes,
    adjustmentData
  );

  if (!period || !companyCode) {
    return;
  }

  try {
    if (adjustmentData.files && adjustmentData.files.length > 0) {
      const attachments = await addAttachmentsApi(adjustmentData.files);
      adjustmentData.files = adjustmentData.files.map((file, index) => ({
        ...file,
        attachmentId: attachments[index].attachmentId,
      }));
    }

    if (openForEdit) {
      await updateAdjustmentApi({
        companyCode,
        period: period.name,
        adjustmentData: {
          ...mapAdjustmentData(adjustmentData),
          bookType,
        },
        adjustmentId,
      });
      dispatch(successNotification('Adjustment updated'));
    } else {
      const response = await addAdjustmentApi({
        companyCode,
        period: period.name,
        adjustmentData: {
          ...mapAdjustmentData(adjustmentData),
          bookType,
        },
      });
      adjustmentId = String(response.adjustmentId);
      dispatch(successNotification('Adjustment added'));
    }
    dispatch({
      type: SAVE_ADJUSTMENT_SUCCESS,
      leaveModalOpen,
      adjustmentId,
    });
    if (!leaveModalOpen) {
      dispatch(hideAdjustmentModalForAddEdit());
    }

    dispatch(selectRow({
      name: agGridConfig.provisionAdjustments.name,
      fields: agGridConfig.provisionAdjustments.fields,
      data: { adjustmentId },
    }));

    await dispatch(fetchProvisionData(companyCode));
    if (callback) {
      callback();
    }
  } catch (error) {
    dispatch({
      type: SAVE_ADJUSTMENT_FAILURE,
    });
    dispatch(errorNotification(error.message));
  }
};

export const removeAdjustment = adjustment => async (dispatch, getState) => {
  dispatch({ type: REMOVE_ADJUSTMENT });
  const removeAdjustmentSuccess = () => ({
    type: REMOVE_ADJUSTMENT_SUCCESS,
  });

  const removeAdjustmentError = () => ({
    type: REMOVE_ADJUSTMENT_FAILURE,
  });

  const period = getState().shared.period;
  const companyCode = getState().shared.companyCode;

  try {
    await removeAdjustmentApi({
      period: period.name,
      companyCode,
      adjustmentId: adjustment.adjustmentId,
    });
    dispatch(successNotification('Adjustment removed'));
    dispatch(hideDeleteModal());
    await dispatch(fetchProvisionData(companyCode));
    dispatch(removeAdjustmentSuccess());
    dispatch(selectRowByRowIndex(adjustment.rowIndex ? adjustment.rowIndex - 1 : 0));
  } catch (error) {
    dispatch(removeAdjustmentError());
    dispatch(errorNotification(error.message));
  }
};

export const showAdjustmentModalForDelete = adjustmentToDelete => dispatch => {
  dispatch(showDeleteModal(adjustmentToDelete));
};

export const {
  showAction: showAttachmentsModal,
  hideAction: hideAttachmentsModal,
} = getModalActions(ModalNames.Attachments);

export const showModalForAttachments = adjustmentForAttachmentsModal => dispatch => {
  dispatch(showAttachmentsModal(adjustmentForAttachmentsModal));
};

export const fetchAdjustmentAttachments = adjustmentId => async (dispatch, getState) => {
  dispatch({ type: FETCH_ADJUSTMENT_ATTACHMENTS });

  const fetchAdjustmentAttachmentsSuccess = adjustmentAttachments => ({
    type: FETCH_ADJUSTMENT_ATTACHMENTS_SUCCESS,
    adjustmentAttachments,
  });

  const fetchAdjustmentAttachmentsFailure = () => ({
    type: FETCH_ADJUSTMENT_ATTACHMENTS_FAILURE,
  });

  const period = getState().shared.period;

  try {
    const adjustmentAttachments = await fetchAdjustmentAttachmentsApi({
      period: period.name,
      adjustmentId,
    });
    dispatch(fetchAdjustmentAttachmentsSuccess(adjustmentAttachments));
  } catch (error) {
    dispatch(fetchAdjustmentAttachmentsFailure());
    dispatch(errorNotification(error.message));
  }
};

export const fetchInitialDataForAdjustmentModal = () => async dispatch => {
  dispatch({ type: FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL });
  try {
    await dispatch(fetchBookTypes());
    await dispatch(fetchAccounts());
    return dispatch({ type: FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL_SUCCESS });
  } catch (error) {
    dispatch({ type: FETCH_INITIAL_DATA_FOR_ADJUSTMENT_MODAL_FAILURE });
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};
